<template>
  <div>
    <h3>
      {{ displayRecentStartups ? 'New companies' : 'All companies' }}
      <el-popover
        v-if="displayRecentStartups"
        :content="`This is a weekly updated list of new startups added to your organization.`"
        trigger="hover"
        width="200"
      >
        <i slot="reference" class="small ion ion-information-circled" />
      </el-popover>
    </h3>

    <startup-list-filters
      v-if="enabledFilters"
      :enabled-filters="enabledFilters"
      :prev-page-filters="filters"
      @change="applyFilters"
    />
    <div v-if="!startups.length && !fetchInProgress" class="alert alert-info">
      <p>There are no startups to show yet</p>
    </div>
    <div v-loading.fullscreen="fetchInProgress" class="container-fluid pane">
      <div>
        <slot name="pre-table"></slot>
        <div v-if="startups.length > 0">
          <div class="flex justify-between">
            <div>
              <PageNumberLinks
                v-show="showPagination"
                :page="page"
                :total="count"
              />
            </div>
            <div class="my-[22px] items-end mr-8">
              <el-form inline>
                <el-form-item label="Sort">
                  <!-- <span class="text-gray-600 font-normal text-sm mr-4">Sort</span> -->
                  <el-radio-group v-model="ordering" class="mt-1">
                    <el-radio v-model="ordering" :label="ORDER_BY_NAME">
                      Alphabetically
                    </el-radio>
                    <el-radio
                      v-model="ordering"
                      :label="ORDER_BY_RECENTLY_UPDATED"
                    >
                      Recently active
                    </el-radio>
                  </el-radio-group>
                </el-form-item>
              </el-form>
            </div>
          </div>

          <slot>
            <StartupListTable
              v-loading="fetchInProgress"
              class="row"
              :startups="startups"
              :shortlist-id="shortlistId"
              show-actions
              @rated="onStartupRated"
            />
          </slot>

          <PageNumberLinks
            v-show="showPagination"
            :page="page"
            :total="count"
          />

          <el-button
            v-if="showDownloadExcel && user.isAdmin"
            class="pull-right margin-top-20"
            @click.prevent="downloadExcelStartupList()"
          >
            <i class="ion ion-android-download"></i>
            Download excel
          </el-button>
        </div>
        <br /><!-- TODO: ask designer to fix this -->
        <slot name="post-table" :startups="startups"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import StartupListFilters from 'modules/startup-list/StartupListFilters.vue'
import StartupListTable from 'modules/startup-list/StartupListTable.vue'
import _, { isArray, isEqual, isObject, transform } from 'lodash'
import http from 'services/http'
import { mapState } from 'vuex'
import { getObfuscatedStartup } from '@/store/startup-model'

import { emptyFilterForm } from '@/store/startup-list'
import { getCorespondingDataArray } from '@/utils/filters'

function difference(origObj, newObj) {
  function changes(newObj, origObj) {
    let arrayIndexCounter = 0
    return transform(newObj, function (result, value, key) {
      if (!isEqual(value, origObj[key])) {
        const resultKey = isArray(origObj) ? arrayIndexCounter++ : key
        result[resultKey] =
          isObject(value) && isObject(origObj[key])
            ? changes(value, origObj[key])
            : value
      }
    })
  }
  return changes(newObj, origObj)
}

const ORDER_BY_NAME = 'NAME'
const ORDER_BY_RECENTLY_UPDATED = 'RECENTLY_UPDATED'

export default {
  components: {
    StartupListFilters,
    StartupListTable,
  },
  props: {
    organizationId: { type: String, default: '' },
    followedOnly: { type: Boolean },
    enabledFilters: { type: [Boolean, Array], default: () => true },
    fetchFilters: { type: Object, default: () => ({}) },
    showDownloadExcel: { type: Boolean, default: true },
    showRecent: { type: Boolean, default: false },
    shouldBlur: { type: Boolean, default: false },
    showroomId: { type: String, default: null },
    shortlistId: { type: String, default: null },
  },
  data() {
    return {
      ORDER_BY_NAME,
      ORDER_BY_RECENTLY_UPDATED,
      ordering: ORDER_BY_RECENTLY_UPDATED,
      filters: {
        organizationId: undefined,
        followed_only: undefined,
      },
    }
  },
  watch: {
    '$route.query.page'(newVal) {
      if (newVal === undefined) return
      this.fetch()
    },
    showRecent() {
      this.fetch()
    },
    ordering() {
      this.fetch()
    },
  },
  provide() {
    return {
      showroomId: this.showroomId,
    }
  },
  created() {
    window.vm = this
  },
  mounted() {
    this.fetch()
  },
  computed: {
    displayRecentStartups() {
      return this.$hasFeature('ORG_NEW_STARTUPS') && this.showRecent
    },
    showPagination() {
      if (this.displayRecentStartups) {
        return this.recentCount > 24
      }
      return this.count > 24
    },
    count() {
      if (this.displayRecentStartups) {
        return this.recentCount
      }
      return this.allCount
    },
    page() {
      if (this.displayRecentStartups) {
        return this.recentPage
      }
      return this.allPage
    },
    ...mapState({
      allFetchInProgress: (s) => s.startupList.fetchInProgress,
      recentFetchInProgress: (s) => s.startupList.recentFetchInProgress,
      allStoreStartups: (s) =>
        _.sortBy(s.startupList.startups, 'is_obfuscated'),
      recentStartups: (s) => s.startupList.recentStartups,
      allPage: (s) => s.startupList.page,
      recentPage: (s) => s.startupList.recentPage,
      allCount: (s) => s.startupList.count,
      recentCount: (s) => s.startupList.recentCount,
      user: (s) => s.user.data,
    }),
    allStartups() {
      if (
        this.shouldBlur &&
        this.$store.state.user.data.hasOrganizationDemoModeEnabled
      ) {
        if (this.followedOnly) {
          return this.allStoreStartups
        }
        const missingToFillPage = Math.max(24 - this.allStoreStartups.length, 0)
        return this.allStoreStartups.concat(
          getObfuscatedStartups(missingToFillPage),
        )
      }
      return this.allStoreStartups
    },
    fetchInProgress() {
      if (this.displayRecentStartups) {
        return this.recentFetchInProgress
      }
      return this.allFetchInProgress
    },
    startups() {
      if (this.displayRecentStartups) {
        return this.recentStartups
      }
      return this.allStartups
    },
  },
  $pusherHandlers: {
    startup: {
      classified(event) {
        if (this.startups.map((s) => s.id).includes(event.id)) {
          this.fetch({ noStartupStopIndicator: true })
        }
      },
      rated(event) {
        if (this.startups.map((s) => s.id).includes(event.id)) {
          this.fetch({ noStartupStopIndicator: true })
        }
      },
    },
  },

  methods: {
    async fetch(extraFilters = {}) {
      const filters = {
        ...this.getFilters(),
        ...this.fetchFilters,
        ...extraFilters,
      }

      if (this.ordering !== ORDER_BY_NAME) {
        filters.ordering = {
          [ORDER_BY_RECENTLY_UPDATED]: '-updated_at',
        }[this.ordering]
      }

      if (
        this.displayRecentStartups &&
        (this.organizationId || this.user.organizationId)
      ) {
        this.fetchRecentStartups(filters)
      }
      if (this.organizationId) {
        const organizationId = this.organizationId
        this.$store.dispatch('startupList/fetchForOrganization', {
          organizationId,
          filters,
        })
      } else {
        this.$store.dispatch('startupList/fetch', filters)
      }
    },

    getFilters() {
      const { page, ...restQuery } = this.$route.query
      // apply extra filters specific to this page
      const filters = {
        ...restQuery,
        ...this.filters,
        page: parseInt(page, 10),
      }

      if (this.user && this.user.isOrganizer) {
        filters.organizationId = this.user.organizationId
      }
      if (this.followedOnly) {
        filters.followed_only = true
      }
      if (this.shortlistId) {
        filters.shortlist_id = this.shortlistId
      }
      return filters
    },

    applyFilters: _.debounce(function (filters) {
      const differenceInFilters = difference(emptyFilterForm(), filters)

      if (this.user.isInvestorOrOrganizer) {
        differenceInFilters.visited = filters.visited
      }

      const findKeysThatNeedChange = [
        'previous_rounds_size',
        'round_size',
        'annual_revenue',
        'monthly_recurring_revenue',
        'profile_progress',
      ].filter((k) => differenceInFilters[k])

      findKeysThatNeedChange.forEach((key) => {
        const sliderMin = filters[key][0]
        const sliderMax = filters[key][1]

        if (key === 'profile_progress') {
          if (sliderMin > 0) {
            differenceInFilters[`${key}__min`] = sliderMin
          }
          if (sliderMax < 100) {
            differenceInFilters[`${key}__max`] = sliderMax
          }
          delete differenceInFilters[key]
          return
        }

        if (sliderMin > 0) {
          differenceInFilters[`${key}__min`] =
            getCorespondingDataArray(key)[sliderMin].value
        }
        if (sliderMax < getCorespondingDataArray(key).length - 1) {
          differenceInFilters[`${key}__max`] =
            getCorespondingDataArray(key)[sliderMax].value
        }

        delete differenceInFilters[key]
      })

      this.filters = { ...differenceInFilters }

      this.$router.push({
        name: this.$route.name,
        query: { ...differenceInFilters },
      })

      // TODO: we probably don't want to update query params at all in the long run
      // we need to give browser some time to update query param
      const self = this
      setTimeout(function () {
        self.fetch()
        const filtersData = JSON.stringify(self.filters)
        http.postAuthed(`/startups/filtered-query`, filtersData)
      }, 500)
    }, 250),

    onStartupRated({ startupId, ratings }) {
      this.$store.commit('startupList/setStartupAttrs', {
        startupId,
        attrs: { ratings },
      })
    },

    downloadExcelStartupList() {
      const filters = { ...this.getFilters(), ...this.fetchFilters }
      const url = '/startups.xlsx'
      http.downloadFile(url, 'startups.xlsx', filters)
    },

    fetchRecentStartups(filters = {}) {
      this.$store.dispatch('startupList/fetchRecent', {
        organizationId: this.organizationId || this.user.organizationId,
        filters,
      })
    },
  },
}

function getObfuscatedStartups(n) {
  return Array(n)
    .fill()
    .map((v, idx) => getObfuscatedStartup())
}
</script>

<style scoped lang="scss">
.startup-list__container {
  padding: 35px 35px;
  background: #fff;
}
</style>
